import styled, { css } from 'styled-components';

import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurMotion,
    getPurpurSpacing,
    getPurpurTypography,
} from '@telialt/common-theme/src/purpurSelectors';

export const RadioButtonLabel = styled.label`
    position: relative;
    display: inline-flex;
    align-items: flex-start;
    min-height: 2.75rem;
`;

export const RadioButtonInput = styled.input.attrs({ type: 'radio' })`
    input& {
        z-index: 1;
        appearance: none;
        cursor: pointer;
        height: 1rem;
        width: 1rem;
        position: absolute;
        transform: translateX(-50%);
        left: ${getPurpurSpacing('150')};
        top: 0;
        margin: 0;
        opacity: 0;
        padding: 0;
        &:checked,
        &:disabled {
            cursor: default;
        }
    }
`;

export const FakeRadioButton = styled.div`
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${getPurpurBorder('radiusFull')};
    transition: box-shadow ${getPurpurMotion('duration150')} ease;
    box-shadow: ${getPurpurColor('borderInteractiveSubtle')} 0px 0px 0px ${getPurpurBorder('widthXs')} inset;
    flex-shrink: 0;
`;

export const FakeRadioDot = styled.div`
    width: 1rem;
    height: 1rem;
    background-color: transparent;
    border-radius: ${getPurpurBorder('radiusFull')};
`;

export const RadioButtonLabelText = styled.span`
    padding-left: ${getPurpurSpacing('150')};
    color: ${getPurpurColor('textDefault')};
    margin: 0;
    font-size: ${getPurpurTypography('scale100')};
    line-height: ${getPurpurTypography('lineHeightLoose')};
    cursor: pointer;
    word-break: break-word;
`;

export const HelperText = styled.div`
    line-height: ${getPurpurTypography('lineHeightLoose')};
    color: ${getPurpurColor('textMedium')};
    margin-top: ${getPurpurSpacing('50')};
`;

export const RadioButtonContainer = styled.div<{ $fullWidth: boolean }>`
    ${RadioButtonInput}:checked ~ ${FakeRadioButton} {
        box-shadow: ${getPurpurColor('textInteractivePrimary')} 0px 0px 0px ${getPurpurBorder('widthXs')} inset;
        ${FakeRadioDot} {
            background-color: ${getPurpurColor('backgroundInteractivePrimary')};
        }
    }

    ${RadioButtonInput}:checked ~ ${RadioButtonLabelText},
    ${RadioButtonInput}:disabled ~ ${RadioButtonLabelText} {
        cursor: default;
    }

    ${RadioButtonLabel}:hover ${RadioButtonInput}:not(:checked):not(:disabled) ~ ${FakeRadioButton} {
        box-shadow: ${getPurpurColor('borderInteractivePrimaryHover')} 0px 0px 0px ${getPurpurBorder('widthSm')} inset;
    }

    ${RadioButtonInput}:focus:focus-visible ~ ${RadioButtonLabelText}::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: ${getPurpurBorder('radiusSm')};
        outline: ${getPurpurBorder('widthSm')} solid ${getPurpurColor('borderInteractiveFocus')};
        outline-offset: ${getPurpurSpacing('25')};
        pointer-events: none;
    }

    ${RadioButtonInput}:disabled ~ ${FakeRadioButton} {
        box-shadow: ${getPurpurColor('borderMedium')} 0px 0px 0px ${getPurpurBorder('widthXs')} inset;
        background-color: ${getPurpurColor('backgroundInteractiveDisabled')};
        ${FakeRadioDot} {
            background-color: ${getPurpurColor('textWeak')};
        }
    }

    ${RadioButtonInput}:disabled ~ ${RadioButtonLabelText} {
        color: ${getPurpurColor('textWeak')};
    }

    & + & {
        margin-top: ${getPurpurSpacing('200')};
    }

    ${({ $fullWidth }) =>
        $fullWidth &&
        css`
            ${RadioButtonLabel}, ${RadioButtonLabelText} {
                width: 100%;
            }
        `}
`;
